"use client";

import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useTranslations } from "next-intl";

import { useOnlineStatus } from "@/app/hooks/useOnlineStatus";
import { useSession } from "@/app/hooks/useSession";
import { useAiUsage } from "@/app/providers/AiUsageProvider";
import { springGentle } from "@/lib/animations";

interface LimitsIndicatorProps {
  onUpgradeClick?: () => void;
}

export function LimitsIndicator({ onUpgradeClick }: LimitsIndicatorProps) {
  const t = useTranslations("settings");
  const tc = useTranslations("common");
  const { isAuthenticated, isPremium } = useSession();
  const { usage, limit, remaining, loaded } = useAiUsage();
  const { isOffline } = useOnlineStatus();

  if (!isAuthenticated) {
    return null;
  }

  if (!loaded) {
    return null;
  }

  const usedPct = limit > 0 ? Math.min(100, (usage / limit) * 100) : 0;

  const barColor = usedPct < 50 ? "bg-emerald-500" : usedPct < 80 ? "bg-yellow-500" : "bg-red-500";

  return (
    <div className="border border-border rounded-lg p-4 flex items-start gap-3">
      <Sparkles className="size-5 text-primary flex-shrink-0 mt-0.5" />
      <div className="flex-1 space-y-2">
        <h3 className="font-medium text-foreground">{t("aiCreditsTitle")}</h3>
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${barColor}`}
            initial={{ width: 0 }}
            animate={{ width: `${usedPct}%` }}
            transition={springGentle}
          />
        </div>
        <p className="text-sm text-muted-foreground">
          {tc("aiUsesRemaining", { remaining, limit })}
        </p>
        {!isPremium && (
          <>
            <p className="text-sm text-muted-foreground">{t("upgradeForMoreCredits")}</p>
            {onUpgradeClick && !isOffline && (
              <button
                type="button"
                onClick={onUpgradeClick}
                className="cursor-pointer text-sm font-medium text-foreground underline hover:no-underline mt-1 inline-block"
              >
                {t("upgradeToPremium")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
